// src/modules/app-views/dataSearch/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import DataSearchHistoric from './pages/Historic';
import ListProvider from 'components/core/ListProvider';
import { ApiOutlined } from '@ant-design/icons';
import { API } from 'constants/Api';
import dataSearchHistoricActionButton from './pages/Historic/actions/actionButton';
import dataSearchHistoricMenuButton from './pages/Historic/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import DataSearchSearch from './pages/Search';
import DataSearchDocument from './pages/Document';
import dataSearchDocumentActionButton from './pages/Document/actions/actionButton';
import dataSearchDocumentDropdownMenu from './pages/Document/actions/dropdownMenu';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import {
  documentCodeItem,
  createdAtItem,
  updatedAtItem,
  emailItem,
  phoneItem,
  locationItem,
  cpfCnpjItem,
} from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'dataSearch';

// Entity configuration - single source of truth for entityType and entityProfile
// Uses dedicated 'search' profile to keep searched entities isolated from other modules
const ENTITY_CONFIG = {
  entityProfile: ['search'],
  entityType: ['person', 'company'],
};

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 1,
  id: MODULE_ID,
  initialPage: 'search',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: null,
  parametersSeed: null,
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  // init is the main page (no endpoint) - Search page
  search: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'page',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props} hideBackToHomeLink={true}>
        <DataSearchSearch />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.ENTITIES.URL,
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: false, // Search page doesn't auto-fetch
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: false,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: null,
      initialValues: {},
      actions: [],
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['queries', 'data_search'],
        showHighlightTitle: 'new_search',
        showHighlightIcon: ApiOutlined,
      },

      card: {
        title: () => null,
        subtitle: () => null,
        column1: () => null,
        column2: () => null,
      },
    },
  },

  // historic page - List of searched entities
  historic: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <DataSearchHistoric />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.DATA_SEARCH.PAGINATION,
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/historic',
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.createdAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['view', 'archive', 'remove'],
      hiddenColumns: ['line'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: [
        'filter',
        'refresh',
        dataSearchHistoricActionButton(),
        dataSearchHistoricMenuButton(),
      ],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['queries', 'data_search'],
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <DataSearchDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: API.ENTITIES.URL,
      params: null,
      query: {
        filter: JSON.stringify(ENTITY_CONFIG),
      },

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {},
      actions: [
        'editRefresh',
        dataSearchDocumentActionButton(),
        dataSearchDocumentDropdownMenu(),
        'return',
      ],
      viewMode: 'auto',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
        placeholder: 'logo',
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['queries', 'data_search'],
      },

      card: {
        title: props => <CardHeaderTitle {...props} placeholder="new_search_result" />,
        subtitle: props => <CardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn {...props} items={[documentCodeItem, createdAtItem, updatedAtItem]} />
        ),
        column2: props => (
          <CardHeaderColumn {...props} items={[emailItem, phoneItem, locationItem]} />
        ),
        column3: props => <CardHeaderColumn {...props} items={[cpfCnpjItem]} />,
        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },
  },
};

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK = {
  init: {
    searchValue: '02934311000153',
    // searchValue: { value: '19981010101' },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, MOCK, preConfigChanges };
